/**
 * ArrowsBackground — legacy SWF parallax-arrows backdrop.
 *
 * Diagonal up-right arrows drift through the chat area. Each instance:
 *   - Starts at a random position OFF-SCREEN (below-left of the stage)
 *     so the arrow scrolls INTO the viewport instead of popping in.
 *   - Random size 10–100% of the asset's natural 114×124.
 *   - Speed scales with size (bigger = faster) → parallax depth feel.
 *   - Final opacity capped at ~0.04 (≈96% transparent) per user spec.
 *
 * Animation has no negative delay and no fade-in tween — arrows are
 * invisible by virtue of physical position, not by alpha. They become
 * visible only when their travel brings them into the stage rectangle.
 *
 * Gated by `prefs.playEffects`.
 */
import { useMemo, useState } from 'react';
import { usePrefs } from '../hooks/usePrefs.js';

interface ArrowConfig {
    id: number;
    /** % size relative to asset natural — 10..100 */
    scalePct: number;
    /** Initial `left` in % of parent. Always negative → off-screen left. */
    leftPct: number;
    /** Initial `bottom` in % of parent. Always negative → off-screen below. */
    bottomPct: number;
    /** Animation duration in seconds. Scales inversely with size. */
    durSec: number;
    /** Final opacity at peak — very low (97% transparent). */
    opacity: number;
}

let nextId = 0;
function randomArrow(): ArrowConfig {
    // Bias toward smaller arrows so most are 10–40%; a few hit 100%.
    const r = Math.random();
    const scalePct = 10 + Math.pow(r, 1.6) * 90;            // 10..100
    // Speed: small = slow (30s travel), large = fast (8s) → parallax.
    const durSec = 30 - (scalePct / 100) * 22;              // 8..30s
    // Opacity: ≈3–5% (was 3–15%; user wants ~97% transparent).
    const opacity = 0.02 + (scalePct / 100) * 0.03;         // 0.02..0.05

    // Start position must always be OFF-SCREEN below-left of the stage so
    // the arrow scrolls INTO view. We use negative percentages of the
    // parent (chat-main-stage) for both axes.
    //
    // The arrow's animation translates +110vw / -110vh over its lifetime,
    // so starting around (-100%..-10%) on each axis means the trajectory
    // crosses the visible rect somewhere in the middle of the cycle.
    return {
        id: ++nextId,
        scalePct,
        leftPct:   -150 + Math.random() * 130,              // -150..-20
        bottomPct: -150 + Math.random() * 130,              // -150..-20
        durSec,
        opacity,
    };
}

const NATURAL_W = 114;
const NATURAL_H = 124;

interface Props {
    count?: number;
}

export function ArrowsBackground({ count = 18 }: Props) {
    const [prefs] = usePrefs();
    const initial = useMemo(() =>
        Array.from({ length: count }, () => randomArrow()),
    [count]);
    const [arrows, setArrows] = useState<ArrowConfig[]>(initial);

    if (!prefs.playEffects) return null;

    return (
        <div className="fx-arrows" aria-hidden="true">
            {arrows.map(a => (
                <img
                    key={a.id}
                    src="/prochat/app/legacy/ffr-arrow.png"
                    alt=""
                    className="fx-arrow"
                    style={{
                        left:   `${a.leftPct}%`,
                        bottom: `${a.bottomPct}%`,
                        width:  `${(NATURAL_W * a.scalePct) / 100}px`,
                        height: `${(NATURAL_H * a.scalePct) / 100}px`,
                        opacity: a.opacity,
                        animationDuration: `${a.durSec}s`,
                        /* No animation-delay → every arrow starts at t=0
                           at its OFF-SCREEN start position. They reach the
                           viewport at staggered times because their start
                           positions and speeds differ. */
                    }}
                    onAnimationIteration={() => {
                        // After a full cycle, reshuffle this arrow's params
                        // so the next sweep uses a new random start.
                        setArrows(prev => prev.map(x => x.id === a.id ? randomArrow() : x));
                    }}
                />
            ))}
        </div>
    );
}
